import type { Permission, PermissionState } from "../types";

/**
 * Ported from apps/admin/lib/permissions.ts. `can()` is still the only thing anything outside
 * this module should ask — hiding a control is not access control, so a gated mutation re-checks
 * with `can()` (against the freshest fetched permission state) before calling the API, same as
 * before.
 *
 * `002`'s per-request `cache()` doesn't have an SPA equivalent to reach for: there's no
 * server-render request boundary to dedupe within. Instead the root route's loader (routes/root.tsx)
 * fetches the permission state once per navigation and every screen reads it from loader data —
 * still "re-read per request" in spirit (FR-011), just expressed as "re-read per route load."
 */

export const ALL_PERMISSIONS: readonly Permission[] = [
  "backoffice.access",
  "catalogue.read",
  "catalogue.administer",
  "pricing.read",
  "pricing.administer",
  "pricing.base.administer",
  "pricing.tiers.readAny",
  "customers.read",
  "customers.terms",
  "customers.credit",
  "customers.administer",
  "orders.read",
  "orders.administer",
  "orders.advance",
  "orders.override",
  "orders.discount",
  "billing.read",
  "billing.administer",
  "billing.creditnote",
  "billing.review",
  "billing.settle",
  "dispatch.assign",
  "integrations.read",
  "integrations.administer",
  "config.administer",
  "inventory.read",
  "inventory.administer",
  "packing.oversight",
  "packing.override",
  "packing.rules.administer",
  "company.user.administer",
  "user.status",
  "company.user.invite",
  "identity.audit.read",
  "identity.matrix.administer",
];

function isPermission(value: string): value is Permission {
  return (ALL_PERMISSIONS as readonly string[]).includes(value);
}

/**
 * A runtime guard, not a hand-written contract — Identity declares no response body on any
 * operation (GAP-4/GAP-5). Unrecognised permission strings are dropped rather than kept, same as
 * before: nothing here ever checks for them, and keeping them would hide a future mismatch.
 */
export function parsePermissions(body: unknown): PermissionState {
  const raw = (body ?? {}) as Record<string, unknown>;
  const role = typeof raw.role === "string" ? raw.role : null;
  const list = Array.isArray(raw.permissions) ? raw.permissions : null;

  if (role === null || list === null || !list.every((p): p is string => typeof p === "string")) {
    throw new Error(
      "GET /api/identity/me/permissions answered without a role and a string permissions array. " +
        "The shell has nothing to gate navigation with (FR-011).",
    );
  }

  const permissions =
    raw.grantsAll === true ? new Set(ALL_PERMISSIONS) : new Set(list.filter(isPermission));
  return { role, permissions };
}

export function can(permissionState: PermissionState, permission: Permission): boolean {
  return permissionState.permissions.has(permission);
}
